export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

function intersect(a: DOMRect, b: DOMRect): DOMRect | null {
  const left = Math.max(a.left, b.left);
  const top = Math.max(a.top, b.top);
  const right = Math.min(a.right, b.right);
  const bottom = Math.min(a.bottom, b.bottom);
  if (right - left < 1 || bottom - top < 1) return null;
  return new DOMRect(left, top, right - left, bottom - top);
}

/**
 * Visible rectangles (relative to `origin`) of every rendered copy of a node.
 * A node can appear on several pages (clip windows) and inside repeated
 * headers; each copy is clipped to its fragment and page body.
 */
export function visibleNodeRects(container: HTMLElement, origin: HTMLElement, nodeId: string): Rect[] {
  const originRect = origin.getBoundingClientRect();
  const out: Rect[] = [];
  const els = container.querySelectorAll<HTMLElement>(`[data-node="${CSS.escape(nodeId)}"]`);
  for (const el of els) {
    if (el.hasAttribute("data-culled")) continue;
    if (el.closest(".bp-repeat-header")) continue;
    let r: DOMRect | null = el.getBoundingClientRect();
    const clip = el.closest<HTMLElement>(".bp-fragment__clip, .bp-page__region");
    if (clip) r = intersect(r, clip.getBoundingClientRect());
    if (!r) continue;
    const body = el.closest<HTMLElement>(".bp-page__body, .bp-page__region");
    if (body) r = intersect(r, body.getBoundingClientRect());
    if (!r) continue;
    out.push({ x: r.left - originRect.left, y: r.top - originRect.top, width: r.width, height: r.height });
  }
  return out;
}

/** The first visible element for a node (used for the block toolbar and inline editing). */
export function firstVisibleElement(container: HTMLElement, nodeId: string): HTMLElement | null {
  const els = container.querySelectorAll<HTMLElement>(`[data-node="${CSS.escape(nodeId)}"]`);
  for (const el of els) {
    if (el.hasAttribute("data-culled") || el.closest(".bp-repeat-header")) continue;
    const r = el.getBoundingClientRect();
    const clip = el.closest<HTMLElement>(".bp-fragment__clip, .bp-page__region");
    if (clip && !intersect(r, clip.getBoundingClientRect())) continue;
    return el;
  }
  return null;
}

/** Finds the node id under a pointer event within the canvas. */
export function nodeIdAt(target: EventTarget | null): { id: string; type: string; el: HTMLElement } | null {
  // Wrappers carry data-key; inner parts carry only data-node, so anchor on the wrapper.
  const el = (target as HTMLElement | null)?.closest?.<HTMLElement>("[data-key]") ?? null;
  if (!el) return null;
  return { id: el.getAttribute("data-node")!, type: el.getAttribute("data-type") ?? "", el };
}
